import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sprout, Shield, ShieldCheck, Crown, User as UserIcon, LogOut, Download, Sparkles } from 'lucide-react';
import { User } from '../types';
import { api } from '../api/client';

interface HeaderProps {
  user: User | null;
  onOpenAuth: () => void;
  onUserChange: (user: User | null) => void;
  currentSlug?: string;
}

export const Header: React.FC<HeaderProps> = ({ user, onOpenAuth, onUserChange, currentSlug }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  const handleLogout = async () => {
    await api.logout();
    onUserChange(null);
  };

  return (
    <header className="header-bar">
      <div className="header-container">
        {/* Brand */}
        <Link to="/" className="brand-link">
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #10b981, #065f46)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 16px rgba(16, 185, 129, 0.4)'
          }}>
            <Sprout size={20} color="#fff" />
          </div>
          <span style={{ letterSpacing: '-0.02em' }}>Watch This Work</span>
          <span className="brand-badge">PWA</span>
        </Link>

        {/* Action Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {isInstallable && (
            <button 
              className="btn btn-outline-emerald btn-sm"
              onClick={handleInstallClick}
              title="Install Watch This Work as a PWA"
            >
              <Download size={14} />
              <span>Install App</span>
            </button>
          )}

          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '4px 10px 4px 6px',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name}
                    style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserIcon size={14} color="#fff" />
                  </div>
                )}
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{user.name}</span>
                {user.is_premium ? (
                  <span className="badge badge-emerald" style={{ padding: '2px 6px', fontSize: '0.68rem' }}>
                    <Crown size={11} /> PRO
                  </span>
                ) : (
                  <span className="badge badge-amber" style={{ padding: '2px 6px', fontSize: '0.68rem' }}>
                    FREE
                  </span>
                )}
              </div>

              <button
                className="btn btn-secondary btn-sm"
                onClick={handleLogout}
                title="Log out"
                style={{ padding: '0.4rem' }}
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={onOpenAuth}>
              <ShieldCheck size={14} />
              <span>Sign In / Pro</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
